import React, { useCallback, useEffect, useState } from 'react';
import { authService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import { useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { confirmacaoDeRevogarDispositivo, fraseDaListaDeSegurancaQueNaoLeu } from '../utils/configuracoes';

interface TrustedDevice {
  id: number;
  label: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

/**
 * Dispositivos em que o usuário marcou "confiar por 30 dias" e que por isso não
 * pedem o código do MFA a cada login. Revogar aqui faz o próximo login naquele
 * navegador voltar a exigir o TOTP.
 */
export default function TrustedDevicesSection({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const confirmarPerigo = useConfirmarPerigo();
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState<number | 'all' | null>(null);
  // D3 (11/10/2026): a lista que não carregava dizia "Nenhum dispositivo confiavel", que é lista de segurança.
  const [erro, setErro] = useState<string | null>(null);
  const confirmar = useConfirm();
  const avisar = useAviso();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const dados = await authService.listTrustedDevices();
      setDevices(dados);
      setErro(null);
      onCountChange?.(dados.length);
    } catch (e) {
      setDevices([]);
      setErro(mensagemDoErroDeCarga(e));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  // D3: revogar UM era sem pergunta e sem catch (falha calada).
  const revokeOne = async (device: TrustedDevice) => {
    if (!(await confirmar(confirmacaoDeRevogarDispositivo(device, device.lastUsedAt ? fmtDate(device.lastUsedAt) : null)))) return;
    setRevoking(device.id);
    try {
      await authService.revokeTrustedDevice(device.id);
      await load();
    } catch (e: any) {
      avisar(`Não revoguei o dispositivo: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setRevoking(null);
    }
  };

  const revokeAll = async () => {
    if (!await confirmarPerigo('Revogar todos os dispositivos confiaveis? O codigo do MFA voltara a ser pedido em todos eles.')) return;
    setRevoking('all');
    try {
      await authService.revokeAllTrustedDevices();
      await load();
    } catch (e: any) {
      avisar(`Não revoguei os dispositivos: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <>
      <p className='text-sm text-slate-400'>
        Navegadores que nao pedem o codigo do MFA no login, por 30 dias. Trocar a senha revoga todos automaticamente.
      </p>

      {loading && <p className='text-sm text-slate-500'>Carregando...</p>}

      {!loading && devices.length > 0 && (
        <div className='space-y-2'>
          {devices.map((device) => (
            <div key={device.id} className='flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3'>
              <div className='flex items-center gap-3'>
                <svg className='w-5 h-5 text-emerald-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                </svg>
                <div>
                  <p className='text-sm text-white font-medium'>{device.label || 'Dispositivo'}</p>
                  <p className='text-xs text-slate-500'>
                    {device.ipAddress && `${device.ipAddress} · `}
                    Confiado em {fmtDate(device.createdAt)}
                    {device.lastUsedAt && ` · Ultimo uso: ${fmtDate(device.lastUsedAt)}`}
                    {` · Expira em ${fmtDate(device.expiresAt)}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => revokeOne(device)}
                disabled={revoking !== null}
                className='p-1.5 text-slate-500 hover:text-red-400 rounded disabled:opacity-50'
                title='Revogar dispositivo'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                </svg>
              </button>
            </div>
          ))}

          <button
            onClick={revokeAll}
            disabled={revoking !== null}
            className='text-xs text-slate-400 hover:text-red-400 disabled:opacity-50'
          >
            {revoking === 'all' ? 'Revogando...' : 'Revogar todos'}
          </button>
        </div>
      )}

      {!loading && erro && (
        <div role='alert' className={`text-sm ${TONE_TEXT.danger} flex items-center gap-3`}>
          <span>{fraseDaListaDeSegurancaQueNaoLeu('os dispositivos confiáveis', erro)}</span>
          <button onClick={load} className='px-3 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-xs'>Tentar de novo</button>
        </div>
      )}

      {!loading && !erro && devices.length === 0 && (
        <p className='text-sm text-slate-500'>
          Nenhum dispositivo confiavel. Marque "Confiar neste dispositivo" ao digitar o codigo do MFA.
        </p>
      )}
    </>
  );
}
